// frontend/src/components/Shop.js

import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getProducts } from '../actions/productActions';
import { Link } from 'react-router-dom';
import Loader from './layouts/Loader';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import { ChevronDown, Filter, SlidersHorizontal, X, ShoppingBag, ShoppingCart } from 'lucide-react';
import MetaData from './layouts/MetaData';

// Updated ProductCard component for Shop.js with proper perPack support
const ProductCard = ({ product }) => {
    if (!product) return null;
   
    const calculateDiscountedPrice = (originalPrice) => {
        try {
            if (!originalPrice) return 0;
            if (!product.discount || !product.discount.active) return originalPrice;
           
            if (product.discount.type === 'percentage') {
                return originalPrice - (originalPrice * (product.discount.value / 100));
            }
            return originalPrice - product.discount.value;
        } catch (error) {
            console.error('Error calculating discount:', error);
            return originalPrice || 0;
        }
    };

    const displayPrice = () => {
        try {
            if (product.priceType === 'perKg') {
                const originalPrice = product.price || 0;
                const finalPrice = calculateDiscountedPrice(originalPrice);
               
                return (
                    <div className="space-y-1">
                        {product.discount?.active && (
                            <span className="text-gray-500 line-through text-xs mr-2">
                                ₹{originalPrice.toFixed(2)}
                            </span>
                        )}
                        <div className="text-[#dc2626] font-bold text-sm">
                            ₹{finalPrice.toFixed(2)}
                            <span className="text-xs font-normal ml-1">/kg</span>
                        </div>
                    </div>
                );
            } else if (product.priceType === 'perDozen') {
                const originalPrice = product.dozenPrice || 0;
                const finalPrice = calculateDiscountedPrice(originalPrice);
               
                return (
                    <div className="space-y-1">
                        {product.discount?.active && (
                            <span className="text-gray-500 line-through text-xs mr-2">
                                ₹{originalPrice.toFixed(2)}/dozen
                            </span>
                        )}
                        <span className="text-[#dc2626] font-bold text-sm">
                            ₹{finalPrice.toFixed(2)}/dozen
                        </span>
                    </div>
                );
            } else if (product.priceType === 'perPack') {
                const originalPrice = product.packPrice || 0;
                const finalPrice = calculateDiscountedPrice(originalPrice);
               
                return (
                    <div className="space-y-1">
                        {product.discount?.active && (
                            <span className="text-gray-500 line-through text-xs mr-2">
                                ₹{originalPrice.toFixed(2)}/pack
                            </span>
                        )}
                        <span className="text-[#dc2626] font-bold text-sm">
                            ₹{finalPrice.toFixed(2)}/pack
                        </span>
                    </div>
                );
            } else if (product.weightOptions && Array.isArray(product.weightOptions) && product.weightOptions.length > 0) {
                // For fixed weight options (500g, 1kg) - Only show visible ones
                const visibleOptions = product.weightOptions.filter(opt => opt.visible);
                
                if (visibleOptions.length === 0) {
                    return (
                        <div className="text-gray-500 text-xs">
                            Currently unavailable
                        </div>
                    );
                }
               
                const prices = visibleOptions.map(opt => {
                    const originalPrice = opt.price;
                    return calculateDiscountedPrice(originalPrice);
                });
               
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);
               
                // Key fix: Only show price range if there are multiple visible options
                if (visibleOptions.length > 1) {
                    return (
                        <div className="text-[#dc2626] font-bold text-sm">
                            ₹{minPrice.toFixed(2)} - ₹{maxPrice.toFixed(2)}
                        </div>
                    );
                } else {
                    // If only one option is visible, just show that price
                    return (
                        <div className="text-[#dc2626] font-bold text-sm">
                            ₹{minPrice.toFixed(2)}
                            <span className="text-xs font-normal ml-1">{visibleOptions[0].weight}</span>
                        </div>
                    );
                }
            }
           
            return (
                <div className="text-[#dc2626] font-bold text-sm">
                    ₹{(product.price || 0).toFixed(2)}
                </div>
            );
        } catch (error) {
            console.error('Error displaying price:', error);
            return <p className="text-gray-500 text-xs">Price unavailable</p>;
        }
    };

    // Helper function to handle different image URL formats
    const getImageUrl = (imageSource) => {
        if (!imageSource) return null;
       
        // If it's already an S3 URL, return it directly
        if (imageSource.includes('amazonaws.com')) {
            return imageSource;
        }
       
        // If it's a relative URL, make it absolute
        if (imageSource.startsWith('/')) {
            return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${imageSource}`;
        }

        // For other URL formats
        return imageSource;
    };

    return (
        <div className="transform transition-transform hover:-translate-y-1">
            <Link to={`/product/${product._id}`} className="block relative">
                <div className="relative h-40 w-full">
                    <div className="w-full h-full overflow-hidden">
                        {product.images && product.images[0] ? (
                            <img
                                src={getImageUrl(product.images[0].image)}
                                alt={product.name || 'Product'}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                    console.error("Image failed to load:", product.images[0].image);
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                }}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <ShoppingBag className="w-10 h-10 text-gray-400" />
                            </div>
                        )}
                    </div>
                    {product.discount?.active && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                            {product.discount.type === 'percentage'
                                ? `${product.discount.value}% OFF`
                                : `₹${product.discount.value} OFF`
                            }
                        </div>
                    )}
                </div>
            </Link>
            <div className="pt-2 flex flex-col items-center text-center">
                <Link to={`/product/${product._id}`} className="block w-full">
                    <h3 className="font-medium text-xs text-gray-800 line-clamp-1 mb-1 hover:text-[#dc2626] transition-colors">
                        {product.name || 'Unnamed Product'}
                    </h3>
                </Link>
               
                <div className="flex flex-col items-center mb-2">
                    {displayPrice()}
                </div>
                <Link
                    to={`/product/${product._id}`}
                    className="inline-block w-3/4 text-center bg-[#dc2626] text-white py-1 rounded-md text-xs hover:bg-[#dc2626]/90 transition-colors mx-auto"
                >
                    View Details
                </Link>
            </div>
        </div>
    );
};

export default function Shop() {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [category, setCategory] = useState('All Categories');
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    // Add this ref to track component mount status
    const isMounted = useRef(true);
    const { products, loading, error, productsCount, resPerPage, filteredProductsCount } = useSelector(state => state.productsState);

    // Updated categories structure with both main categories and subcategories
    const allCategories = [
        'All Categories',
        'All Mutton Products',
        'All Chicken Products',
        'All Egg Products',
        'Mutton - Fresh Cuts',
        'Mutton - Premium Cuts',
        'Chicken - Fresh Cuts',
        'Chicken - Special Cuts',
        'Egg \-- Fresh Egg'
    ];

    // Map for main categories to their subcategories
    const categoryMap = {
        'All Mutton Products': ['Mutton - Fresh Cuts', 'Mutton - Premium Cuts'],
        'All Chicken Products': ['Chicken - Fresh Cuts', 'Chicken - Special Cuts'],
        'All Egg Products': ['Egg \-- Fresh Egg']
    };

    // Set up cleanup function to handle component unmounting
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        const categoryFromUrl = searchParams.get('category');
        if (categoryFromUrl) {
            setCategory(categoryFromUrl);
        } else {
            setCategory('All Categories');
        }
    }, [searchParams]);

    useEffect(() => {
        // Prevent dispatch if component is unmounting
        if (!isMounted.current) return;
       
        if (error) {
            toast.error(error);
            return;
        }
       
        let categoryToFilter = '';
       
        // This is the key fix - handle parent categories differently
        if (category === 'All Mutton Products') {
            // For API we need to pass something that will match both Mutton subcategories
            categoryToFilter = 'Mutton -'; // This prefix will match both "Mutton - Fresh Cuts" and "Mutton - Premium Cuts"
        } else if (category === 'All Chicken Products') {
            // For API we need to pass something that will match both Chicken subcategories
            categoryToFilter = 'Chicken -'; // This prefix will match both "Chicken - Fresh Cuts" and "Chicken - Special Cuts"
        } else if (category === 'All Egg Products') {
            // For API we need to pass something that will match egg subcategories
            categoryToFilter = 'Egg'; // This prefix will match "Egg \-- Fresh Egg" and any future egg subcategories
        } else if (category === 'Mutton') {
            // Handle the main Mutton category
            categoryToFilter = 'Mutton -';
        } else if (category === 'Chicken') {
            // Handle the main Chicken category
            categoryToFilter = 'Chicken -';
        } else if (category === 'Egg') {
            // Handle the main Egg category
            categoryToFilter = 'Egg';
        } else if (category !== 'All Categories') {
            // If it's a specific subcategory, use it directly
            categoryToFilter = category;
        }
       
        const fetchProducts = async () => {
            try {
                console.log('Fetching products with category filter:', categoryToFilter);
                // Force refresh to get latest data
                await dispatch(getProducts('', priceRange, categoryToFilter, currentPage, true));
            } catch (err) {
                if (isMounted.current) {
                    console.error("Error fetching products:", err);
                }
            }
        };
       
        fetchProducts();
       
    }, [currentPage, priceRange, category, error, dispatch]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handlePriceChange = (type, value) => {
        const numValue = value === '' ? '' : parseInt(value, 10);
        setPriceRange(prev => {
            if (type === 'min') {
                return [numValue, prev[1]];
            } else {
                return [prev[0], numValue];
            }
        });
    };

    const applyPriceFilter = () => {
        const min = priceRange[0] === '' ? 0 : priceRange[0];
        const max = priceRange[1] === '' ? 1000 : priceRange[1];
        setPriceRange([min, max]);
       
        let categoryToFilter = '';
        if (category === 'All Mutton Products') {
            categoryToFilter = 'Mutton -';
        } else if (category === 'All Chicken Products') {
            categoryToFilter = 'Chicken -';
        } else if (category === 'All Egg Products') {
            categoryToFilter = 'Egg';
        } else if (category === 'Mutton') {
            categoryToFilter = 'Mutton -';
        } else if (category === 'Chicken') {
            categoryToFilter = 'Chicken -';
        } else if (category === 'Egg') {
            categoryToFilter = 'Egg';
        } else if (category !== 'All Categories') {
            categoryToFilter = category;
        }
       
        if (isMounted.current) {
            dispatch(getProducts('', [min, max], categoryToFilter, 1));
            setCurrentPage(1);
        }
    };

    const getCategoryTitle = () => {
        if (category === 'All Categories') {
            return 'Shop';
        } else if (category === 'All Mutton Products' || category === 'Mutton') {
            return 'Shop \-- Mutton Products';
        } else if (category === 'All Chicken Products' || category === 'Chicken') {
            return 'Shop \-- Chicken Products';
        } else if (category === 'All Egg Products' || category === 'Egg') {
            return 'Shop \-- Egg Products';
        } else {
            return `Shop \-- ${category}`;
        }
    };

    const handleCategoryChange = (selectedCategory) => {
        setCategory(selectedCategory);
        setCurrentPage(1);
       
        let categoryToFilter = '';
        if (selectedCategory === 'All Mutton Products') {
            categoryToFilter = 'Mutton -';
        } else if (selectedCategory === 'All Chicken Products') {
            categoryToFilter = 'Chicken -';
        } else if (selectedCategory === 'All Egg Products') {
            categoryToFilter = 'Egg';
        } else if (selectedCategory === 'Mutton') {
            categoryToFilter = 'Mutton -';
        } else if (selectedCategory === 'Chicken') {
            categoryToFilter = 'Chicken -';
        } else if (selectedCategory === 'Egg') {
            categoryToFilter = 'Egg';
        } else if (selectedCategory !== 'All Categories') {
            categoryToFilter = selectedCategory;
        }
       
        if (isMounted.current) {
            dispatch(getProducts('', priceRange, categoryToFilter, 1));
        }
    };

    const count = filteredProductsCount || productsCount;

    return (
        <div className="container mx-auto px-6 py-8" style={{
            backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
            backgroundSize: 'auto',
            backgroundPosition: 'center'
        }}>
            <MetaData title={getCategoryTitle()} />
           
            <div className="flex items-center justify-between mb-8">
                <div className="w-1/4">
                    <button
                        onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                        className="lg:hidden flex items-center gap-2 text-[#dc2626] bg-white px-4 py-2 rounded-lg shadow-sm"
                    >
                        <SlidersHorizontal className="h-5 w-5" />
                        <span className="text-xs">Filters</span>
                    </button>
                </div>
                <h1 className="text-2xl font-['Crimson_Text'] font-bold text-[#dc2626] text-center flex-1">
                    {getCategoryTitle()}
                </h1>
                <div className="w-1/4 flex justify-end">
                    <Link
                        to="/cart"
                        className="flex items-center gap-2 text-gray-600 hover:text-[#dc2626] transition-colors"
                    >
                        <ShoppingCart className="w-5 h-5" />
                        <span className="text-xs">Cart</span>
                    </Link>
                </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Filters - Desktop */}
                <div className="hidden lg:block w-72 flex-shrink-0">
                    <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                        <h4 className="text-lg font-semibold text-[#dc2626] mb-4 font-['Crimson_Text']">Categories</h4>
                        <select
                            className="w-full p-3 border border-gray-200 rounded-lg mb-6 focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] text-gray-600 text-xs"
                            value={category}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                        >
                            {allCategories.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                        <h4 className="text-lg font-semibold text-[#dc2626] mb-4 font-['Crimson_Text']">Price Range</h4>
                        <div className="space-y-4">
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <input
                                        type="number"
                                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] text-gray-600 text-xs"
                                        placeholder="Min"
                                        value={priceRange[0]}
                                        onChange={(e) => handlePriceChange('min', e.target.value)}
                                    />
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="number"
                                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] text-gray-600 text-xs"
                                        placeholder="Max"
                                        value={priceRange[1]}
                                        onChange={(e) => handlePriceChange('max', e.target.value)}
                                    />
                                </div>
                            </div>
                            <button
                                onClick={applyPriceFilter}
                                className="w-full bg-[#dc2626] text-white py-2 rounded-lg hover:bg-[#dc2626]/90 transition-colors text-xs"
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Filters */}
                {isMobileFiltersOpen && (
                    <div className="lg:hidden fixed inset-0 z-50 bg-white">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-[#dc2626] font-['Crimson_Text']">Filters</h3>
                                <button
                                    onClick={() => setIsMobileFiltersOpen(false)}
                                    className="text-gray-500 hover:text-[#dc2626] transition-colors"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                           
                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-[#dc2626] mb-3 font-['Crimson_Text']">Categories</h4>
                                    <select
                                        className="w-full p-3 border border-gray-200 rounded-lg focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] text-gray-600 text-xs"
                                        value={category}
                                        onChange={(e) => handleCategoryChange(e.target.value)}
                                    >
                                        {allCategories.map(cat => (
                                            <option key={cat} value={cat}>
                                                {cat}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-[#dc2626] mb-3 font-['Crimson_Text']">Price Range</h4>
                                    <div className="flex gap-4">
                                        <input
                                            type="number"
                                            className="w-1/2 p-3 border border-gray-200 rounded-lg focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] text-gray-600 text-xs"
                                            placeholder="Min"
                                            value={priceRange[0]}
                                            onChange={(e) => handlePriceChange('min', e.target.value)}
                                        />
                                        <input
                                            type="number"
                                            className="w-1/2 p-3 border border-gray-200 rounded-lg focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] text-gray-600 text-xs"
                                            placeholder="Max"
                                            value={priceRange[1]}
                                            onChange={(e) => handlePriceChange('max', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <button
                                className="w-full mt-8 bg-[#dc2626] text-white py-3 rounded-lg font-medium hover:bg-[#dc2626]/90 transition-colors text-xs"
                                onClick={() => {
                                    applyPriceFilter();
                                    setIsMobileFiltersOpen(false);
                                }}
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                )}

                {/* Products Grid */}
                <div className="flex-1">
                    {loading ? (
                        <div className="flex items-center justify-center min-h-[400px]">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#dc2626]"></div>
                        </div>
                    ) : products && products.length === 0 ? (
                        <div className="text-center py-12">
                            <ShoppingBag className="w-16 h-16 text-[#dc2626] mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Products Found</h3>
                            <p className="text-gray-500 text-xs">Try adjusting your filters or browse all categories</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                {products && products.map(product => (
                                    <ProductCard key={product._id} product={product} />
                                ))}
                            </div>
                            {resPerPage < count && (
                                <div className="flex justify-center mt-10">
                                    <Pagination
                                        activePage={currentPage}
                                        itemsCountPerPage={resPerPage}
                                        totalItemsCount={count}
                                        onChange={handlePageChange}
                                        nextPageText={'Next'}
                                        prevPageText={'Prev'}
                                        firstPageText={'First'}
                                        lastPageText={'Last'}
                                        itemClass="px-3 py-2 rounded-lg mx-1 border hover:bg-[#dc2626] hover:text-white transition-colors"
                                        linkClass="text-gray-600 hover:text-white text-xs"
                                        activeClass="!bg-[#dc2626] !text-white"
                                        activeLinkClass="!text-white"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}